import React, { useState } from 'react';
import { array, func, string, bool } from 'prop-types';
import useWithA11y from 'library/hooks/use-with-a11y';
import Input from 'library/components/form-components/input';
import KeyCodes from 'library/helpers/key-codes';
import {
  ControlsContainer,
  SearchIcon,
  SortIcon,
  FilterIcon,
  StyledSearchContainer,
  StyledSearchInnerContainer,
  StyledToolTip,
  StyledToolTipLabel,
} from 'apps/journey/pages/career-exploration/components/filters/styled-filter-controls';
import SortDropdown from './sort-dropdown';

const FilterControls = ({
  sortOptions,
  searchValue,
  sortValues,
  handleSearch,
  handleSort,
  filterCallback,
  sortControl,
  filterControl,
  searchControl,
  searchAriaLabel,
}) => {
  const [searchActive, setSearchActive] = useState(searchValue.length > 0);
  const [sortActive, setSortActive] = useState(false);
  const { isTabbing } = useWithA11y();

  const handleSearchActive = (newStatus) => {
    setSearchActive(newStatus);
    if (!newStatus && searchValue.length) {
      handleSearch({
        target: {
          value: '',
        },
      });
    }
  };

  const closeSearch = () => {
    searchValue.length === 0 && handleSearchActive(!searchActive);
  };

  const toggleSort = () => {
    setSortActive(!sortActive);
    // toggle the other control if filter control is between
    filterControl && setSearchActive(searchActive && sortActive);
  };

  const toggleSearch = () => {
    if (searchActive && searchValue.length > 0) {
      return;
    }
    setSearchActive(!searchActive);
    filterControl && setSortActive(sortActive && searchActive);
  };

  const handleKeyPress = (e, controlType) => {
    e.preventDefault();
    if (KeyCodes.POSITIVE_AFFIRMATIONS.includes(e.which)) {
      if (controlType === 'sort') {
        toggleSort();
      } else if (controlType === 'search') {
        toggleSearch();
      } else if (controlType === 'filter') {
        filterCallback();
      }
    }
  };

  return (
    <ControlsContainer>
      {searchControl &&
        (searchActive ? (
          <StyledSearchContainer>
            <StyledSearchInnerContainer>
              <Input
                name="searchValue"
                label="Search"
                type="text"
                value={searchValue}
                onChange={handleSearch}
                onBlurAction={closeSearch}
                focus
              />
              <SearchIcon
                aria-label="Close search"
                tabIndex={0}
                isTabbing={isTabbing}
                onClick={closeSearch}
                onKeyPress={(e) => handleKeyPress(e, 'search')}
              />
            </StyledSearchInnerContainer>
          </StyledSearchContainer>
        ) : (
          <StyledToolTip
            aria-label={searchAriaLabel || 'Search Occupations by keywords'}
            isTabbing={isTabbing}
            tabIndex={0}
            onKeyPress={(e) => handleKeyPress(e, 'search')}
          >
            <SearchIcon onClick={toggleSearch} />
            <StyledToolTipLabel>Search</StyledToolTipLabel>
          </StyledToolTip>
        ))}
      {filterControl && (
        <StyledToolTip
          aria-label="Filter occupations by education level, salary etc."
          isTabbing={isTabbing}
          tabIndex={0}
          onKeyPress={(e) => handleKeyPress(e, 'filter')}
        >
          <FilterIcon onClick={filterCallback} />
          <StyledToolTipLabel>Filter</StyledToolTipLabel>
        </StyledToolTip>
      )}
      {sortControl &&
        (sortActive ? (
          <SortDropdown
            active={sortActive}
            toggleSort={toggleSort}
            sortOptions={sortOptions}
            sortValues={sortValues}
            handleChange={handleSort}
          />
        ) : (
          <StyledToolTip
            isTabbing={isTabbing}
            aria-label="Sort occupations by title or salary"
            tabIndex={0}
            onKeyPress={(e) => handleKeyPress(e, 'sort')}
          >
            <SortIcon onClick={toggleSort} />
            <StyledToolTipLabel>Sort</StyledToolTipLabel>
          </StyledToolTip>
        ))}
    </ControlsContainer>
  );
};

FilterControls.propTypes = {
  searchValue: string,
  sortValues: array,
  handleSearch: func,
  handleSort: func,
  filterCallback: func,
  sortOptions: array,
  sortControl: bool,
  searchControl: bool,
  filterControl: bool,
  searchAriaLabel: string,
};

export default React.memo(FilterControls);
